import { useNavigate } from 'react-router-dom';
import useInView from '../../../hooks/useInView';

const financingOptions = [
  { icon: 'ri-percent-line', title: '0% APR Financing', desc: '12, 18, and 24-month no-interest options on qualifying systems.' },
  { icon: 'ri-time-line', title: 'Fast Approvals', desc: 'Apply in minutes. Most approvals within 60 seconds.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Low Monthly Payments', desc: 'Spread the cost over time to fit your budget.' },
  { icon: 'ri-coupon-3-line', title: 'Government Rebates', desc: 'IRA tax credits up to $3,200/year on qualifying upgrades.' },
  { icon: 'ri-leaf-line', title: 'Energy Savings Offset', desc: 'Equipment savings can offset monthly payments from day one.' },
  { icon: 'ri-shield-check-line', title: 'Flexible Terms', desc: '36 to 120-month financing plans for major installations.' },
];

const FinancingSection = () => {
  const navigate = useNavigate();
  const { ref: leftRef, inView: leftInView } = useInView();
  const { ref: rightRef, inView: rightInView } = useInView();

  return (
    <section id="financing" className="py-10 md:py-16 bg-[#EEF5FF]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div
            ref={leftRef}
            className={`transition-all duration-800 ease-out ${leftInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}
          >
            <p className="text-[#C41E3A] text-sm font-bold uppercase tracking-widest mb-3">Financing &amp; Payments</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#111827] mb-5 sm:mb-6">
              Flexible Financing for Every Budget
            </h2>
            <p className="text-base sm:text-lg text-[#6B7280] mb-6 sm:mb-8 leading-relaxed">
              Don&apos;t let budget stand in the way of home comfort. Renova offers flexible financing solutions with competitive rates, fast approvals, and monthly payment options that fit your lifestyle.
            </p>

            <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
              {[
                { val: '$0', label: 'Down on qualifying systems' },
                { val: '0% APR', label: 'for up to 24 months' },
                { val: '$3,200', label: 'max annual IRA tax credits' },
                { val: '60 sec', label: 'average approval time' },
              ].map((stat, i) => (
                <div
                  key={stat.label}
                  style={{ transitionDelay: `${100 + i * 80}ms` }}
                  className={`flex items-center space-x-4 py-3 border-b border-[#E2E8F0] transition-all duration-700 ease-out ${leftInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                >
                  <span className="text-2xl sm:text-3xl font-bold text-[#1A56A0] w-24 sm:w-28 flex-shrink-0">{stat.val}</span>
                  <span className="text-sm sm:text-base text-[#6B7280]">{stat.label}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/financing')}
                className="bg-[#C41E3A] text-white px-7 py-3.5 rounded-lg text-sm font-semibold hover:bg-[#A01830] transition-colors whitespace-nowrap cursor-pointer"
              >
                Apply for Financing
              </button>
              <button
                onClick={() => navigate('/rebates-incentives')}
                className="border border-[#1A56A0] text-[#1A56A0] px-7 py-3.5 rounded-lg text-sm font-semibold hover:bg-[#1A56A0] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                View Rebates &amp; Incentives
              </button>
            </div>
          </div>

          <div ref={rightRef} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {financingOptions.map((f, i) => (
              <div
                key={f.title}
                style={{ transitionDelay: `${i * 90}ms` }}
                className={`bg-white rounded-xl p-5 sm:p-6 border border-[#E2E8F0] transition-all duration-700 ease-out ${rightInView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'}`}
              >
                <div className="w-11 h-11 bg-[#1A56A0]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${f.icon} text-xl text-[#1A56A0]`} />
                </div>
                <h4 className="text-sm font-bold text-[#111827] mb-2">{f.title}</h4>
                <p className="text-xs text-[#6B7280] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinancingSection;
